import { useState, useCallback, useEffect } from "react";
import { LimitMeter } from "../components/LimitMeter";
import { Kbd } from "../components/Kbd";
import { CountdownChip } from "../components/CountdownChip";
import { SignalTradeTicketModal } from "../components/SignalTradeTicketModal";
import { SymbolLabel } from "../components/SymbolLabel";
import { fmtPct, fmtPx, fmtSigned, fmtSignedPct, fmtUSD } from "../lib/format";
import { assetClassOf } from "../lib/assetClass";
import { relativeUntil } from "../lib/coolingOff";
import type {
  CryptoSummary,
  CryptoValidationRun,
  ForexConfig,
  ForexPair,
  GeneratedSignal,
  GenerateSignalsResult,
  RiskSnapshot,
} from "../api/dataSource";
import type {
  Account,
  Basket,
  EventRow,
  Investment,
  LimitItem,
  Pending,
  PendingConfigChange,
  Position,
  PositionReassessment,
  PositionTrade,
  RiskLimit,
  SystemHealthRow,
  TradePreview,
  View,
} from "../types";

type Props = {
  onNav: (view: View) => void;
  onConfigTab: (tab: string) => void;
  onOpenApproval: (approvalId: string) => void;
  onQuickApprove: (approvalId: string) => void;
  pendingConfigChanges: PendingConfigChange[];
  onCancelPendingChange: (id: string) => void;
  // Phase 5 D-Live Slice 4: live data from useDataSource. The
  // arrays may be empty when the backend has nothing yet (no
  // broker registered, no trades, no approvals); the screen
  // surfaces empty-state copy in those cases.
  accounts: Account[];
  pending: Pending[];
  positions: Position[];
  positionReassessments?: PositionReassessment[];
  positionTrades: PositionTrade[];
  investments: Investment[];
  baskets: Basket[];
  // Phase 5 (d): previously hard-coded fixture imports; now props.
  // riskLimits → limitItems derived here (caps from DB; used=0
  //   until intraday tracking lands in a future phase).
  riskLimits: RiskLimit[];
  listRiskLimits: (brokerAccountId?: string) => Promise<RiskLimit[]>;
  getRiskSnapshot: (brokerAccountId: string) => Promise<RiskSnapshot>;
  // systemHealth → Dashboard System tile (maps /health subsystems).
  systemHealth: SystemHealthRow[];
  // recentEvents → Dashboard Recent Events panel.
  recentEvents: EventRow[];
  // Phase 8: composite of crypto paper-engine status + top watchlist
  // proximity. null while loading or if both crypto endpoints failed.
  cryptoSummary: CryptoSummary | null;
  cryptoValidationRuns?: CryptoValidationRun[];
  // Phase 9-UI: forex gate + seeded pair reference for the Forex tile.
  forexConfig: ForexConfig | null;
  forexPairs: ForexPair[];
  /**
   * Operator-initiated signal run. Calls POST /api/v1/signals/generate,
   * optionally queues signals as 8-hour Approval rows, then refreshes
   * the pending-approvals list so new rows appear immediately.
   */
  onGenerateSignals: () => Promise<GenerateSignalsResult>;
  autoApprovalsEnabled: boolean;
  autoApprovalsMinConfidence: number;
  autoApprovalsUpdating: boolean;
  onAutoApprovalsChange: (
    enabled: boolean,
    minConfidence: number,
  ) => Promise<void>;
  /** Reprice a skipped generated signal into a fresh pending approval. */
  onRepriceSignal: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<string>;
  onPreviewSignal: (
    signal: GeneratedSignal,
    riskDollars?: string,
  ) => Promise<{
    preview: TradePreview;
    actionable: boolean;
    issues: string[];
  }>;
  /** Halt all new trade approvals (TDD §7.4 user-initiated halt). */
  onHaltAll?: (reason?: string) => Promise<void>;
  /** Resume approvals after a user-initiated halt. */
  onResumeAll?: () => Promise<void>;
};

const signalKey = (signal: GeneratedSignal): string =>
  `${signal.symbol}-${signal.strategyId}-${signal.barDate}`;

const isStopBreachedRepriceError = (message: string): boolean =>
  message.includes("must be above stop") ||
  message.includes("must be below stop");

// AI/Det divergence tiers per UX §8.6.3:
//   |Δ| ≤ 15 → (agree)
//   |Δ| 16–25 → (differ)
//   |Δ| > 25 → ⚠ diverge
// Express eligibility additionally requires |Δ| ≤ 15 (UX §8.2.1).
const divergenceLabel = (
  ai: number,
  det: number,
): { text: string; className: "agree" | "differ" | "diverge" } => {
  const d = Math.abs(ai - det);
  if (d <= 15) {
    return { text: "(agree)", className: "agree" };
  }
  if (d <= 25) {
    return { text: "(differ)", className: "differ" };
  }
  return { text: "⚠ diverge", className: "diverge" };
};

// Risk-display label per PRD §5.9.2 + UX §8.6.7. Live trades carry the
// REAL suffix so the trader cannot confuse a live readout for paper at
// a glance.
const riskLabel = (t: Pending): string => {
  if (t.account === "live") {
    return `${fmtUSD(t.risk)} REAL`;
  }
  if (t.account === "paper") {
    return `paper ${fmtUSD(t.risk)}`;
  }
  // Investment trades show allocation %, not per-trade risk (no stops
  // on long-term holdings). Fixtures don't cover this path yet; the
  // approval modal commit will add the proper renderer.
  return `${fmtUSD(t.risk)}`;
};

const signalPlan = (signal: GeneratedSignal): string => {
  const target = signal.targets[0];
  return target === undefined
    ? `${fmtPx(signal.entry)} / ${fmtPx(signal.stop)}`
    : `${fmtPx(signal.entry)} / ${fmtPx(signal.stop)} -> ${fmtPx(target)}`;
};

const validationCheckLabel = (value: boolean | null): string => {
  if (value === true) return "pass";
  if (value === false) return "fail";
  return "n/a";
};

const shortDate = (iso: string | null): string => {
  if (!iso) return "-";
  return iso.slice(0, 10);
};

const riskUsageItems = (
  limits: RiskLimit[],
  labelPrefix: "Live" | "Paper",
  snapshot: RiskSnapshot | null,
  account: Account | undefined,
  positions: Position[],
): LimitItem[] => {
  const rlMap = Object.fromEntries(limits.map((l) => [l.key, l]));
  const accountPositions = account
    ? positions.filter((p) => p.brokerAccountId === account.brokerAccountId)
    : [];
  const dailyFallback =
    account && account.equity > 0
      ? Math.max(0, -account.todayPL) / account.equity * 100
      : 0;
  const dailyUsed = snapshot?.dailyLossUsedPct ?? dailyFallback;
  const weeklyUsed = snapshot?.weeklyLossUsedPct ?? 0;
  const openUsed = snapshot?.openPositionsUsed ?? accountPositions.length;
  const sectorUsed = snapshot?.sectorConcentrationUsedPct ?? 0;
  const stateFor = (used: number, cap: number): LimitItem["state"] => {
    if (!Number.isFinite(used) || !Number.isFinite(cap) || cap <= 0) {
      return "idle";
    }
    const ratio = used / cap;
    if (ratio >= 1) return "bad";
    if (ratio >= 0.8) return "warn";
    return "ok";
  };
  return (
    [
      rlMap["daily_loss"] && {
        used: Number(dailyUsed.toFixed(2)),
        cap: rlMap["daily_loss"].value,
        label: `${labelPrefix} · Daily loss`,
        state: stateFor(dailyUsed, rlMap["daily_loss"].value),
        unit: "%" as const,
      },
      rlMap["weekly_loss"] && {
        used: Number(weeklyUsed.toFixed(2)),
        cap: rlMap["weekly_loss"].value,
        label: `${labelPrefix} · Weekly loss`,
        state: stateFor(weeklyUsed, rlMap["weekly_loss"].value),
        unit: "%" as const,
      },
      rlMap["max_open"] && {
        used: openUsed,
        cap: rlMap["max_open"].value,
        label: `${labelPrefix} · Open positions`,
        state: stateFor(openUsed, rlMap["max_open"].value),
        unit: "count" as const,
      },
      rlMap["sector_concentration"] && {
        used: Number(sectorUsed.toFixed(2)),
        cap: rlMap["sector_concentration"].value,
        label: snapshot?.topSector
          ? `${labelPrefix} · Sector top (${snapshot.topSector})`
          : `${labelPrefix} · Sector top`,
        state: stateFor(sectorUsed, rlMap["sector_concentration"].value),
        unit: "%" as const,
      },
    ] as (LimitItem | false | undefined)[]
  ).filter((x): x is LimitItem => Boolean(x));
};

const RiskUsageTile = ({
  title,
  items,
  account,
  error,
  onAdjust,
}: {
  title: string;
  items: LimitItem[];
  account?: Account;
  error?: string | null;
  onAdjust: () => void;
}) => (
  <div className="tp-tile">
    <div className="tp-tile-head">
      <span className="label">{title}</span>
      <a className="action" onClick={onAdjust}>
        Adjust caps →
      </a>
    </div>
    <div className="tp-tile-body">
      {account ? (
        <>
          {error && (
            <div
              className="tp-muted"
              style={{ color: "var(--tp-warn)", marginBottom: 8 }}
            >
              {error}
            </div>
          )}
          {items.length > 0 ? (
            <LimitMeter items={items} />
          ) : (
            <div className="tp-muted">No risk limits configured.</div>
          )}
        </>
      ) : (
        <div className="tp-muted">No account registered.</div>
      )}
    </div>
  </div>
);

export const DashboardScreen = ({
  onNav,
  onConfigTab,
  onOpenApproval,
  onQuickApprove,
  pendingConfigChanges,
  onCancelPendingChange,
  accounts,
  pending,
  positions,
  positionReassessments = [],
  positionTrades,
  investments,
  baskets,
  riskLimits,
  listRiskLimits,
  getRiskSnapshot,
  systemHealth,
  recentEvents,
  cryptoSummary,
  forexConfig,
  forexPairs,
  cryptoValidationRuns = [],
  onGenerateSignals,
  autoApprovalsEnabled,
  autoApprovalsMinConfidence,
  autoApprovalsUpdating,
  onAutoApprovalsChange,
  onRepriceSignal,
  onPreviewSignal,
  onHaltAll,
  onResumeAll,
}: Props) => {
  // ---- Generate-signals button state ----
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState<GenerateSignalsResult | null>(
    null,
  );
  const [genError, setGenError] = useState<string | null>(null);
  const [autoApprovalError, setAutoApprovalError] = useState<string | null>(
    null,
  );
  const [autoApprovalMinConfidenceDraft, setAutoApprovalMinConfidenceDraft] =
    useState(String(autoApprovalsMinConfidence));

  useEffect(() => {
    setAutoApprovalMinConfidenceDraft(String(autoApprovalsMinConfidence));
  }, [autoApprovalsMinConfidence]);
  const [repricingKey, setRepricingKey] = useState<string | null>(null);
  const [ticketSignal, setTicketSignal] = useState<GeneratedSignal | null>(
    null,
  );
  const [liveRiskLimits, setLiveRiskLimits] = useState<RiskLimit[] | null>(
    null,
  );
  const [paperRiskLimits, setPaperRiskLimits] = useState<RiskLimit[] | null>(
    null,
  );
  const [liveRiskSnapshot, setLiveRiskSnapshot] =
    useState<RiskSnapshot | null>(null);
  const [paperRiskSnapshot, setPaperRiskSnapshot] =
    useState<RiskSnapshot | null>(null);
  const [riskLimitsError, setRiskLimitsError] = useState<string | null>(null);

  // ---- Halt / resume button state ----
  const [halting, setHalting] = useState(false);
  const [haltError, setHaltError] = useState<string | null>(null);

  const handleHaltAll = useCallback(async () => {
    if (!onHaltAll) return;
    setHalting(true);
    setHaltError(null);
    try {
      await onHaltAll("operator_request");
    } catch (e) {
      setHaltError(e instanceof Error ? e.message : "Halt failed");
    } finally {
      setHalting(false);
    }
  }, [onHaltAll]);

  const handleResumeAll = useCallback(async () => {
    if (!onResumeAll) return;
    setHalting(true);
    setHaltError(null);
    try {
      await onResumeAll();
    } catch (e) {
      setHaltError(e instanceof Error ? e.message : "Resume failed");
    } finally {
      setHalting(false);
    }
  }, [onResumeAll]);

  const handleGenerateSignals = useCallback(async () => {
    setGenerating(true);
    setGenResult(null);
    setGenError(null);
    try {
      const r = await onGenerateSignals();
      setGenResult(r);
    } catch (e) {
      setGenError(e instanceof Error ? e.message : "Signal run failed");
    } finally {
      setGenerating(false);
    }
  }, [onGenerateSignals]);

  const handleAutoApprovalsChange = useCallback(
    async (enabled: boolean, minConfidence = autoApprovalsMinConfidence) => {
      setAutoApprovalError(null);
      try {
        await onAutoApprovalsChange(enabled, minConfidence);
      } catch (e) {
        setAutoApprovalError(
          e instanceof Error ? e.message : "Auto-queue update failed",
        );
      }
    },
    [autoApprovalsMinConfidence, onAutoApprovalsChange],
  );

  const commitAutoApprovalMinConfidence = useCallback(async () => {
    const parsed = Number(autoApprovalMinConfidenceDraft);
    if (!Number.isInteger(parsed) || parsed < 0 || parsed > 100) {
      setAutoApprovalError("Minimum confidence must be 0-100");
      setAutoApprovalMinConfidenceDraft(String(autoApprovalsMinConfidence));
      return;
    }
    await handleAutoApprovalsChange(autoApprovalsEnabled, parsed);
  }, [
    autoApprovalMinConfidenceDraft,
    autoApprovalsEnabled,
    autoApprovalsMinConfidence,
    handleAutoApprovalsChange,
  ]);

  const handleRepriceSignal = useCallback(
    async (signal: GeneratedSignal, riskDollars: string) => {
      const key = signalKey(signal);
      setRepricingKey(key);
      setGenError(null);
      try {
        const approvalId = await onRepriceSignal(signal, riskDollars);
        setTicketSignal(null);
        setGenResult((curr) =>
          curr
            ? {
                ...curr,
                approvalsSkipped: Math.max(0, curr.approvalsSkipped - 1),
                signals: curr.signals.filter(
                  (s) =>
                    !(
                      s.symbol === signal.symbol &&
                      s.strategyId === signal.strategyId &&
                      s.barDate === signal.barDate
                    ),
                ),
              }
            : curr,
        );
        onOpenApproval(approvalId);
        return approvalId;
      } catch (e) {
        const message = e instanceof Error ? e.message : "Reprice failed";
        if (isStopBreachedRepriceError(message)) {
          setGenResult((curr) =>
            curr
              ? {
                  ...curr,
                  approvalsSkipped: Math.max(0, curr.approvalsSkipped - 1),
                  signals: curr.signals.filter((s) => signalKey(s) !== key),
                }
              : curr,
          );
        }
        setGenError(message);
        throw e;
      } finally {
        setRepricingKey(null);
      }
    },
    [onOpenApproval, onRepriceSignal],
  );

  // Live accounts replace the fixture lookup. When the backend
  // hasn't registered a broker yet, these are undefined and the
  // strip falls back to empty-state copy.
  const live =
    accounts.find((a) => a.kind === "trading-live") ??
    accounts.find((a) => a.id === "live");
  const paper =
    accounts.find(
      // Phase 9-UI fix: explicit equity allowlist. The prior
      // `!== "crypto"` blacklist would have silently adopted a forex
      // paper account as "the" equity paper card — the same
      // coercion-to-equity bug class Phase 9a fixed backend-side.
      // Legacy rows with no assetClass still count as equity.
      (a) =>
        a.kind === "trading-paper" &&
        (a.assetClass ?? "equity") === "equity",
    ) ??
    accounts.find((a) => a.id === "paper");
  const invest = accounts.find((a) => a.id === "invest");

  useEffect(() => {
    let cancelled = false;
    const fetchAccountRiskLimits = async () => {
      setRiskLimitsError(null);
      const errors: string[] = [];
      try {
        const [nextLive, nextPaper] = await Promise.all([
          live?.brokerAccountId
            ? listRiskLimits(live.brokerAccountId)
            : Promise.resolve(riskLimits),
          paper?.brokerAccountId
            ? listRiskLimits(paper.brokerAccountId)
            : Promise.resolve<RiskLimit[] | null>(null),
        ]);
        if (cancelled) return;
        setLiveRiskLimits(nextLive.length > 0 ? nextLive : riskLimits);
        setPaperRiskLimits(
          nextPaper && nextPaper.length > 0
            ? nextPaper
            : paper
              ? riskLimits
              : null,
        );
      } catch (e) {
        if (cancelled) return;
        errors.push(
          e instanceof Error ? e.message : "Failed to load account risk limits",
        );
        setLiveRiskLimits(riskLimits);
        setPaperRiskLimits(paper ? riskLimits : null);
      }
      const loadSnapshot = async (
        brokerAccountId: string | undefined,
      ): Promise<RiskSnapshot | null> => {
        if (!brokerAccountId) return null;
        try {
          return await getRiskSnapshot(brokerAccountId);
        } catch (e) {
          errors.push(
            e instanceof Error ? e.message : "Failed to load account risk usage",
          );
          return null;
        }
      };
      const [nextLiveSnapshot, nextPaperSnapshot] = await Promise.all([
        loadSnapshot(live?.brokerAccountId),
        loadSnapshot(paper?.brokerAccountId),
      ]);
      if (cancelled) return;
      setLiveRiskSnapshot(nextLiveSnapshot);
      setPaperRiskSnapshot(nextPaperSnapshot);
      setRiskLimitsError(errors.length > 0 ? errors.join("; ") : null);
    };
    void fetchAccountRiskLimits();
    return () => {
      cancelled = true;
    };
  }, [
    listRiskLimits,
    getRiskSnapshot,
    live?.brokerAccountId,
    paper?.brokerAccountId,
    paper,
    riskLimits,
  ]);

  const liveLimitItems = riskUsageItems(
    liveRiskLimits ?? riskLimits,
    "Live",
    liveRiskSnapshot,
    live,
    positions,
  );
  const paperLimitItems = riskUsageItems(
    paperRiskLimits ?? (paper ? riskLimits : []),
    "Paper",
    paperRiskSnapshot,
    paper,
    positions,
  );
  const openRiskConfig = () => {
    onConfigTab("risk");
    onNav("config");
  };

  const livePending = pending.filter((p) => p.account === "live");
  const paperPending = pending.filter((p) => p.account === "paper");
  const unmatchedGeneratedSignals =
    genResult?.signals.filter(
      (signal) =>
        !pending.some(
          (p) => p.sym === signal.symbol && p.strategy === signal.strategyId,
        ),
    ) ?? [];
  const repricableSignals = genResult && genResult.approvalsSkipped > 0
    ? unmatchedGeneratedSignals.slice(-genResult.approvalsSkipped)
    : [];
  const ptOverdue = positionTrades.filter((p) => p.reviewOverdueDays);
  const highPositionReviews = positionReassessments.filter(
    (row) => row.status === "active" && row.severity === "high",
  );
  const invOverdue = investments.filter(
    (i) => i.reviewStatus === "overdue" || i.reviewStatus === "due",
  );

  // Phase 8 — crypto dashboard tile derivations. Pending approvals
  // for crypto are identified by the `-USD` / `-USDC` symbol suffix
  // (matches the CryptoScreen's isCryptoSymbol() convention).
  // assetClass-first (Phase 9-UI); the suffix heuristic remains only
  // as the fallback inside assetClassOf for rows predating the field.
  const cryptoPendingCount = pending.filter(
    (p) => assetClassOf(p) === "crypto",
  ).length;
  const forexPendingCount = pending.filter(
    (p) => assetClassOf(p) === "forex",
  ).length;
  const cryptoOpenIntents =
    cryptoSummary?.engineStatus?.exitIntentsByStatus?.["open"] ?? 0;
  const latestCryptoValidation = cryptoValidationRuns[0] ?? null;

  return (
    <div data-screen-label="01 Dashboard">
      <div className="tp-page-head">
        <h1>Dashboard</h1>
        <div className="meta">Tue · 14:32 ET · session +2h 02m</div>
      </div>

      {/* Account strip */}
      <div className="tp-acct-row" style={{ marginBottom: 14 }}>
        {accounts.length === 0 ? (
          <div
            className="tp-acct-card"
            style={{
              gridColumn: "1 / -1",
              padding: "20px",
              color: "var(--tp-fg-3)",
              textAlign: "center",
            }}
          >
            No broker accounts configured yet. Run the first-run
            wizard from the System tab to register a broker; the
            account snapshot will appear here once the broker
            adapter reports back.
          </div>
        ) : (
          [live, paper, invest]
            .filter((a): a is Account => Boolean(a))
            .map((a) => (
              <div key={a.brokerAccountId} className={"tp-acct-card " + a.kind}>
                <div className="head">
                  <span className={"tp-acct-tag " + a.kind}>{a.label}</span>
                  <span className="broker num">
                    {a.broker} · {a.mask}
                  </span>
                </div>
                <div className="equity num">{fmtPx(a.equity)}</div>
                <div className="meta">
                  <span
                    className={"num " + (a.todayPL >= 0 ? "pos" : "neg")}
                  >
                    {fmtSigned(Math.round(a.todayPL))}{" "}
                    {a.balanceSource === "asset_class_sleeve" ||
                    a.balanceSource === "asset_class_exposure"
                      ? "sleeve"
                      : a.balanceSource === "shared_broker_residual"
                        ? "broker"
                      : "today"} ·{" "}
                    {fmtPct(a.todayPLPct)}
                  </span>
                </div>
                <div className="sub">
                  {a.id === "live" && a.pdtSlots && a.equityStop && (
                    <>
                      {a.strategiesBound} strategy bound · PDT{" "}
                      {a.pdtSlots.used}/{a.pdtSlots.cap} · stop at{" "}
                      {fmtPx(a.equityStop.value)}
                    </>
                  )}
                  {a.id === "paper" && (
                    <>
                      {a.balanceSource === "asset_class_sleeve" ||
                      a.balanceSource === "asset_class_exposure" ? (
                        <>
                          {a.assetClass ?? "equity"} sleeve · broker{" "}
                          <span className="num">
                            {fmtPx(a.brokerEquity ?? a.equity)}
                          </span>
                        </>
                      ) : a.balanceSource === "shared_broker_residual" ? (
                        <>
                          shared broker balance · crypto shown separately
                        </>
                      ) : (
                        <>
                          {a.strategiesBound} strategies bound · since-start{" "}
                          <span className="num pos">
                            {fmtSigned(Math.round(a.sinceStartPL))}
                          </span>
                        </>
                      )}
                    </>
                  )}
                  {a.id === "invest" &&
                    a.cashSleevePct !== undefined && (
                      <>
                        cash sleeve {a.cashSleevePct}% · 5y vs VTI{" "}
                        <span
                          className={
                            "num " +
                            ((a.vsVTI_5y ?? 0) >= 0 ? "pos" : "neg")
                          }
                        >
                          {fmtSignedPct(a.vsVTI_5y ?? 0)}
                        </span>
                        <span style={{ color: "var(--tp-fg-4)" }}>
                          {" "}
                          · YTD vs VTI {fmtSignedPct(a.vsVTI_YTD ?? 0)}
                        </span>
                      </>
                    )}
                </div>
              </div>
            ))
        )}
      </div>

      {/* Pending config-change banner — UX §3.7 + §13.2.2. Persistent on
         the Dashboard until activation or cancellation. Cancel routes
         through window.confirm() per UX §13.2.3. */}
      {pendingConfigChanges.map((p) => (
        <div
          key={p.id}
          className="tp-banner info"
          role="status"
          aria-live="polite"
        >
          <span className="icon">ⓘ</span>
          <span>
            <b>Pending config change</b> · {p.label}{" "}
            <span className="num">
              {p.currentValue}
              {p.unit === "%" ? "%" : ""} → {p.newValue}
              {p.unit === "%" ? "%" : ""}
            </span>{" "}
            activates {p.activatesAtDisplay} ({relativeUntil(p.activatesAtMs)}).{" "}
            <a
              className="action"
              style={{ color: "var(--tp-accent-hover)", cursor: "pointer" }}
              onClick={() => {
                if (
                  window.confirm(
                    "Cancel this pending change? The change will not activate. You can re-submit later.",
                  )
                ) {
                  onCancelPendingChange(p.id);
                }
              }}
            >
              cancel
            </a>
          </span>
        </div>
      ))}

      {/* Equity-stop reminder (informational; the §15.12 trigger banner
         only fires when the threshold is actually breached). */}
      {live?.equityStop && (
        <div className="tp-banner warn" role="status" aria-live="polite">
          <span className="icon">⚠</span>
          <span>
            <b>Live · Weekly loss usage at 46%</b>. Equity stop fires at{" "}
            {fmtPx(live.equityStop.value)} ({live.equityStop.pct}% of base).
            One session under that floor halts new entries until next Monday.
          </span>
        </div>
      )}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.1fr 1fr 1fr",
          gap: 14,
          marginBottom: 14,
        }}
      >
        {/* Account-specific risk usage. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <RiskUsageTile
            title="Live account · Risk usage"
            account={live}
            items={liveLimitItems}
            error={riskLimitsError}
            onAdjust={openRiskConfig}
          />
          <RiskUsageTile
            title="Paper account · Risk usage"
            account={paper}
            items={paperLimitItems}
            error={riskLimitsError}
            onAdjust={openRiskConfig}
          />
        </div>

        {/* Open day/swing */}
        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Open day/swing ({positions.length})</span>
            <a className="action" onClick={() => onNav("trades")}>
              Day trades →
            </a>
          </div>
          <div className="tp-tile-body" style={{ padding: 0 }}>
            <table className="tp-table">
              <tbody>
                {positions.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      style={{
                        color: "var(--tp-fg-3)",
                        textAlign: "center",
                        padding: "24px 14px",
                      }}
                    >
                      No open positions. Approvals you confirm
                      will appear here once the broker fills them.
                    </td>
                  </tr>
                ) : null}
                {positions.map((p) => (
                  <tr key={p.sym}>
                    <td className="ticker">
                      <span className="tp-symbol-ticker">{p.sym}</span>{" "}
                      <span className="tp-acct-pill live">live</span>{" "}
                      <span
                        style={{
                          color: "var(--tp-fg-4)",
                          fontFamily: "var(--tp-font-sans)",
                          fontWeight: 400,
                          fontSize: 11,
                        }}
                      >
                        {p.qty} sh
                      </span>
                      {p.companyName ? (
                        <div className="tp-symbol-company">{p.companyName}</div>
                      ) : null}
                    </td>
                    <td className="num right">{fmtPx(p.last)}</td>
                    <td
                      className={
                        "num right " + (p.pl >= 0 ? "pos" : "neg")
                      }
                    >
                      {fmtSigned(Math.round(p.pl))}
                      <div
                        style={{ fontSize: 10, color: "var(--tp-fg-4)" }}
                      >
                        {fmtPct(p.plPct)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Long-term overview */}
        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Long-term overview</span>
            <a className="action" onClick={() => onNav("investments")}>
              Investments →
            </a>
          </div>
          <div className="tp-tile-body">
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--tp-fg-3)",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                  }}
                >
                  Position trades
                </div>
                <div
                  className="num"
                  style={{ fontSize: 18, fontWeight: 600 }}
                >
                  {positionTrades.length} open
                </div>
                {ptOverdue.length > 0 ? (
                  <div
                    className="num"
                    style={{ fontSize: 11, color: "var(--tp-warn)" }}
                  >
                    {ptOverdue.length} review overdue
                  </div>
                ) : (
                  <div
                    className="num"
                    style={{ fontSize: 11, color: "var(--tp-fg-3)" }}
                  >
                    all reviews current
                  </div>
                )}
                <div
                  className="num"
                  style={{
                    fontSize: 11,
                    color:
                      highPositionReviews.length > 0
                        ? "var(--tp-danger)"
                        : "var(--tp-fg-3)",
                    marginTop: 4,
                  }}
                >
                  {positionReassessments.length} position reviews
                  {highPositionReviews.length > 0
                    ? ` · ${highPositionReviews.length} high`
                    : ""}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--tp-fg-3)",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                  }}
                >
                  Investments
                </div>
                <div
                  className="num"
                  style={{ fontSize: 18, fontWeight: 600 }}
                >
                  {investments.length} positions
                </div>
                {invOverdue.length > 0 ? (
                  <div
                    className="num"
                    style={{ fontSize: 11, color: "var(--tp-warn)" }}
                  >
                    {invOverdue.length} review due
                  </div>
                ) : (
                  <div
                    className="num"
                    style={{ fontSize: 11, color: "var(--tp-fg-3)" }}
                  >
                    reviewed
                  </div>
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--tp-fg-3)",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                  }}
                >
                  Cash sleeve
                </div>
                <div
                  className="num"
                  style={{ fontSize: 18, fontWeight: 600, color: "var(--tp-fg-3)" }}
                >
                  — {/* investment portfolio not yet configured */}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: 11,
                    color: "var(--tp-fg-3)",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                  }}
                >
                  Research
                </div>
                <div
                  className="num"
                  style={{ fontSize: 18, fontWeight: 600, color: "var(--tp-fg-3)" }}
                >
                  — {/* AI cost tracking not yet wired */}
                </div>
                <div
                  className="num"
                  style={{ fontSize: 11, color: "var(--tp-fg-3)" }}
                >
                  this month
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pending approvals */}
      <div className="tp-tile" style={{ marginBottom: 14 }}>
        <div className="tp-tile-head">
          <span className="label">Pending approvals ({pending.length})</span>
          <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {/* Inline feedback from the last generate run */}
            {genResult && !generating && (
              <span style={{ fontSize: 11, color: "var(--tp-good)" }}>
                {genResult.signalsGenerated} signal
                {genResult.signalsGenerated !== 1 ? "s" : ""},{" "}
                {genResult.longSignals} long · {genResult.shortSignals} short ·{" "}
                {genResult.hedgeWatchSignals} hedge · min{" "}
                {genResult.autoApprovalConfidenceThreshold},{" "}
                {genResult.approvalsCreated} approval
                {genResult.approvalsCreated !== 1 ? "s" : ""} queued
                {genResult.approvalsSkipped > 0
                  ? ` (${genResult.approvalsSkipped} need reprice)`
                  : ""}
              </span>
            )}
            {genError && !generating && (
              <span style={{ fontSize: 11, color: "var(--tp-bad)" }}>
                {genError}
              </span>
            )}
            {autoApprovalError && (
              <span style={{ fontSize: 11, color: "var(--tp-bad)" }}>
                {autoApprovalError}
              </span>
            )}
            <label
              className="tp-approval-toggle"
              title="Generated signals are queued as approval rows when enabled"
            >
              <input
                type="checkbox"
                checked={autoApprovalsEnabled}
                disabled={autoApprovalsUpdating}
                onChange={(e) => {
                  void handleAutoApprovalsChange(e.currentTarget.checked);
                }}
              />
              <span className="lbl">Auto-queue approvals</span>
              <span className="state">
                {autoApprovalsEnabled ? "On" : "Off"}
              </span>
            </label>
            <label
              className="tp-approval-toggle"
              title="Auto-queued approvals require signal score and AI confidence at or above this value"
            >
              <span className="lbl">Min confidence</span>
              <input
                type="number"
                min={0}
                max={100}
                step={1}
                value={autoApprovalMinConfidenceDraft}
                disabled={autoApprovalsUpdating}
                onChange={(e) => {
                  setAutoApprovalMinConfidenceDraft(e.currentTarget.value);
                }}
                onBlur={() => {
                  void commitAutoApprovalMinConfidence();
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.currentTarget.blur();
                  }
                }}
                style={{
                  width: 48,
                  background: "transparent",
                  color: "var(--tp-fg)",
                  border: "0",
                  fontFamily: "var(--tp-font-mono)",
                  fontSize: 11,
                  textAlign: "right",
                }}
              />
            </label>
            <button
              onClick={() => void handleGenerateSignals()}
              disabled={generating}
              style={{
                fontSize: 11,
                padding: "3px 10px",
                borderRadius: 4,
                border: "1px solid var(--tp-border)",
                background: generating
                  ? "var(--tp-bg-2)"
                  : "var(--tp-accent)",
                color: generating ? "var(--tp-fg-3)" : "#fff",
                cursor: generating ? "not-allowed" : "pointer",
                fontFamily: "var(--tp-font-sans)",
                fontWeight: 500,
                whiteSpace: "nowrap",
              }}
            >
              {generating ? "Running…" : "Run signals"}
            </button>
            <span style={{ fontSize: 11, color: "var(--tp-fg-3)" }}>
              <span style={{ marginRight: 8 }}>{livePending.length} live</span>
              <span style={{ marginRight: 8 }}>
                {paperPending.length} paper
              </span>
              <Kbd k="⌘1" /> quick-approves first row
            </span>
          </span>
        </div>
        <div className="tp-tile-body" style={{ padding: 0 }}>
          <table className="tp-table">
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Account</th>
                <th>Strategy</th>
                <th className="right">Entry / Stop</th>
                <th className="right">Risk</th>
                <th className="center">Confidence</th>
                <th className="right">Action</th>
              </tr>
            </thead>
            <tbody>
              {pending.length === 0 && repricableSignals.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      color: "var(--tp-fg-3)",
                      textAlign: "center",
                      padding: "24px 14px",
                    }}
                  >
                    No pending approvals. Strategy ticks fire at session
                    open (9:30 ET); approvals will appear here for
                    confirmation as they're generated.
                  </td>
                </tr>
              ) : (
                <>
                {pending.map((t) => {
                  const scoreKnown = t.scoreKnown !== false;
                  const lbl = scoreKnown ? divergenceLabel(t.ai, t.det) : null;
                  return (
                    <tr key={t.approvalId}>
                      <td className="ticker">
                        <span className="tp-symbol-ticker">{t.sym}</span>
                        {t.basket && (
                          <span
                            className="tp-basket-pill"
                            title={t.basket.name}
                          >
                            {t.basket.name}
                          </span>
                        )}
                        {t.companyName ? (
                          <div className="tp-symbol-company">{t.companyName}</div>
                        ) : null}
                      </td>
                      <td>
                        <span
                          className={
                            "tp-acct-pill " +
                            (t.account === "live" ? "live" : "paper")
                          }
                        >
                          {t.account === "live" ? "live" : "paper"}
                        </span>
                      </td>
                      <td>
                        <span
                          style={{
                            fontFamily: "var(--tp-font-mono)",
                            fontSize: 12,
                            color: "var(--tp-fg-2)",
                          }}
                        >
                          {t.strategy}
                        </span>
                      </td>
                      <td className="num right">
                        {fmtPx(t.entry)}{" "}
                        <span style={{ color: "var(--tp-bad)" }}>
                          / {fmtPx(t.stop)}
                        </span>
                      </td>
                      <td className="num right neg">
                        {riskLabel(t)}{" "}
                        <span
                          style={{
                            color: "var(--tp-fg-4)",
                            fontSize: 11,
                          }}
                        >
                          · {t.riskPct.toFixed(2)}%
                        </span>
                      </td>
                      <td className="center">
                        {scoreKnown && lbl ? (
                          <span className="tp-conf">
                            <span className="ai">{t.ai}</span>
                            <span style={{ color: "var(--tp-fg-4)" }}>/</span>
                            <span className="det">{t.det}</span>
                            <span className={lbl.className}>
                              Δ{t.ai - t.det} {lbl.text}
                            </span>
                          </span>
                        ) : (
                          <span
                            style={{
                              color: "var(--tp-fg-3)",
                              fontSize: 11,
                            }}
                          >
                            not recorded
                          </span>
                        )}
                      </td>
                      <td className="right">
                        <span
                          style={{
                            display: "inline-flex",
                            gap: 8,
                            alignItems: "center",
                            justifyContent: "flex-end",
                          }}
                        >
                          <CountdownChip initialSeconds={t.expiresInSec} />
                          {t.mode === "express" ? (
                            <button
                              type="button"
                              className="tp-btn tp-btn-secondary tp-btn-sm"
                              onClick={() => onQuickApprove(t.approvalId)}
                            >
                              Quick approve ⚡
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="tp-btn tp-btn-secondary tp-btn-sm"
                              onClick={() => onOpenApproval(t.approvalId)}
                            >
                              Review →
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {repricableSignals.map((signal) => {
                  const rowKey = signalKey(signal);
                  return (
                    <tr key={`signal-${rowKey}`}>
                      <td className="ticker">
                        <SymbolLabel
                          symbol={signal.symbol}
                          companyName={signal.companyName}
                        />
                      </td>
                      <td>
                        <span className="tp-acct-pill paper">signal</span>
                      </td>
                      <td>
                        <span
                          style={{
                            fontFamily: "var(--tp-font-mono)",
                            fontSize: 12,
                            color: "var(--tp-fg-2)",
                          }}
                        >
                          {signal.strategyId}
                        </span>
                      </td>
                      <td className="num right">{signalPlan(signal)}</td>
                      <td className="num right">
                        <span style={{ color: "var(--tp-warn)" }}>
                          needs reprice
                        </span>
                      </td>
                      <td className="center">
                        <span className="tp-conf">
                          <span className="ai">
                            {signal.aiConfidence ?? "-"}
                          </span>
                          <span style={{ color: "var(--tp-fg-4)" }}>/</span>
                          <span className="det">{signal.score}</span>
                        </span>
                      </td>
                      <td className="right">
                        <button
                          type="button"
                          className="tp-btn tp-btn-secondary tp-btn-sm"
                          disabled={repricingKey === rowKey}
                          onClick={() => setTicketSignal(signal)}
                        >
                          {repricingKey === rowKey ? "Queueing..." : "Review"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {ticketSignal && (
        <SignalTradeTicketModal
          signal={ticketSignal}
          onClose={() => setTicketSignal(null)}
          onPreview={onPreviewSignal}
          onConfirm={(riskDollars) =>
            handleRepriceSignal(ticketSignal, riskDollars)
          }
        />
      )}

      {/* Bottom row: Recent events · System · Crypto · Baskets */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.3fr 1fr 1fr 1fr",
          gap: 14,
        }}
      >
        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Recent events</span>
            <a className="action" onClick={() => onNav("system")}>
              System log →
            </a>
          </div>
          <div className="tp-tile-body">
            {recentEvents.length === 0 ? (
              <div
                style={{ padding: "12px 0", color: "var(--tp-fg-3)", fontSize: 12 }}
              >
                No events yet. Activity will appear here once the system
                starts processing signals.
              </div>
            ) : (
              recentEvents.slice(0, 6).map((e, i) => (
                <div key={i} className="tp-evt">
                  <span className="time">{e.time}</span>
                  <span className="type">{e.type}</span>
                  <span className="summary">{e.summary}</span>
                  <span className="actor">{e.actor}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* System tile (UX §5.2.7) */}
        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">System</span>
            <a className="action" onClick={() => onNav("system")}>
              Detail →
            </a>
          </div>
          <div className="tp-tile-body">
            {systemHealth.length === 0 ? (
              <div
                style={{ padding: "12px 0", color: "var(--tp-fg-3)", fontSize: 12 }}
              >
                Checking system status…
              </div>
            ) : (
              systemHealth.map((s) => (
                <div key={s.key} className="tp-system-row">
                  <span className={`dot dot-${s.state}`} />
                  <span className="lbl">{s.label}</span>
                  <span className="meta">{s.lastSeen}</span>
                </div>
              ))
            )}
            {/* TDD §7.4 / PRD §4.12: user-accessible global halt button */}
            {(onHaltAll || onResumeAll) && (
              <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap" }}>
                <button
                  className="tp-btn tp-btn--danger tp-btn--sm"
                  onClick={() => void handleHaltAll()}
                  disabled={halting}
                  title="Halt all new trade approvals immediately"
                >
                  {halting ? "…" : "Halt All"}
                </button>
                <button
                  className="tp-btn tp-btn--sm"
                  onClick={() => void handleResumeAll()}
                  disabled={halting}
                  title="Resume approvals after a user-initiated halt"
                >
                  {halting ? "…" : "Resume"}
                </button>
                {haltError && (
                  <span style={{ color: "var(--tp-danger, #c0392b)", fontSize: 11 }}>
                    {haltError}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Phase 8 — Crypto tile (24/7 paper engine + watchlist proximity).
            Surfaces auto-engine status, open exit intents, and the top
            three watchlist symbols by proximity-to-trigger so the
            operator gets a glanceable status without leaving the
            dashboard. Deep-link to the full Crypto screen on click. */}
        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Crypto</span>
            <a className="action" onClick={() => onNav("crypto")}>
              Open →
            </a>
          </div>
          <div className="tp-tile-body" style={{ padding: "8px 0" }}>
            {cryptoSummary === null ? (
              <div
                style={{ padding: "12px 0", color: "var(--tp-fg-3)", fontSize: 12 }}
              >
                Loading crypto status…
              </div>
            ) : cryptoSummary.engineStatus === null &&
              cryptoSummary.topWatchlist.length === 0 ? (
              <div
                style={{ padding: "12px 0", color: "var(--tp-fg-3)", fontSize: 12 }}
              >
                Crypto stack unavailable. Configure Coinbase / Alpaca crypto
                credentials in Config → AI to enable.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {cryptoSummary.engineStatus && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--tp-fg-3)",
                          textTransform: "uppercase",
                          letterSpacing: ".08em",
                        }}
                      >
                        Auto engine
                      </div>
                      <div
                        className="num"
                        style={{
                          fontSize: 15,
                          fontWeight: 600,
                          color: cryptoSummary.engineStatus.autoTradingEnabled
                            ? "var(--tp-good)"
                            : "var(--tp-warn)",
                        }}
                      >
                        {cryptoSummary.engineStatus.autoTradingEnabled
                          ? "On"
                          : "Paused"}
                      </div>
                      {cryptoSummary.engineStatus
                        .autoTradingDisabledReason && (
                        <div
                          style={{
                            fontSize: 10,
                            color: "var(--tp-warn)",
                            marginTop: 2,
                          }}
                        >
                          {
                            cryptoSummary.engineStatus
                              .autoTradingDisabledReason
                          }
                        </div>
                      )}
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--tp-fg-3)",
                          textTransform: "uppercase",
                          letterSpacing: ".08em",
                        }}
                      >
                        Risk · Cap
                      </div>
                      <div className="num" style={{ fontSize: 13 }}>
                        {cryptoSummary.engineStatus.riskPerTradePct}%
                        <span style={{ color: "var(--tp-fg-3)", fontSize: 11 }}>
                          {" "}/ {cryptoSummary.engineStatus.maxOpenTrades} open
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {(cryptoPendingCount > 0 || cryptoOpenIntents > 0) && (
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      fontSize: 11,
                      color: "var(--tp-fg-3)",
                    }}
                  >
                    {cryptoPendingCount > 0 && (
                      <span>
                        <span style={{ color: "var(--tp-warn)" }}>
                          {cryptoPendingCount}
                        </span>{" "}
                        pending
                      </span>
                    )}
                    {cryptoOpenIntents > 0 && (
                      <span>
                        <span style={{ color: "var(--tp-fg-1)" }}>
                          {cryptoOpenIntents}
                        </span>{" "}
                        open intent{cryptoOpenIntents !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                )}
                {latestCryptoValidation && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 8,
                      fontSize: 11,
                      color: "var(--tp-fg-3)",
                    }}
                  >
                    <span>
                      Validation {shortDate(latestCryptoValidation.createdAt)}
                    </span>
                    <span>
                      {validationCheckLabel(latestCryptoValidation.passed)}
                    </span>
                  </div>
                )}
                {cryptoSummary.topWatchlist.length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--tp-fg-3)",
                        textTransform: "uppercase",
                        letterSpacing: ".08em",
                        marginBottom: 4,
                      }}
                    >
                      Near trigger
                    </div>
                    <div
                      style={{ display: "flex", flexDirection: "column", gap: 4 }}
                    >
                      {cryptoSummary.topWatchlist.map((row) => (
                        <div
                          key={row.symbol}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontSize: 12,
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>{row.symbol}</span>
                          <span
                            className="num"
                            style={{ color: "var(--tp-fg-3)" }}
                          >
                            {row.distancePct !== null
                              ? `${row.distancePct}%`
                              : "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Phase 9-UI — Forex tile. Slim pre-9b status: activation
            gate, readiness one-liner, pending count, seeded pair
            count. Deep-links to the Forex screen. */}
        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Forex</span>
            <a className="action" onClick={() => onNav("forex")}>
              Open →
            </a>
          </div>
          <div className="tp-tile-body" style={{ padding: "8px 0" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span style={{ fontSize: 12, color: "var(--tp-fg-2)" }}>
                  Trading gate
                </span>
                <span
                  className={`tp-status ${forexConfig?.forexTradingEnabled ? "ok" : "warn"}`}
                >
                  <span className="dot" />
                  {forexConfig === null
                    ? "unknown"
                    : forexConfig.forexTradingEnabled
                      ? "enabled"
                      : "disabled"}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--tp-fg-3)" }}>
                Foundation shipped (Phase 9a). Broker adapter, calendar, and
                margin model arrive with Phase 9b; strategies with 9c.
              </div>
              <div
                className="num"
                style={{ fontSize: 12, color: "var(--tp-fg-2)" }}
              >
                {forexPairs.length} pairs seeded ·{" "}
                {forexPendingCount} pending approval
                {forexPendingCount === 1 ? "" : "s"}
              </div>
            </div>
          </div>
        </div>

        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Crypto Validation</span>
            <span
              className="action"
              style={{
                color: latestCryptoValidation?.passed
                  ? "var(--tp-good)"
                  : latestCryptoValidation
                    ? "var(--tp-danger)"
                    : "var(--tp-fg-3)",
              }}
            >
              {latestCryptoValidation
                ? latestCryptoValidation.status.toUpperCase()
                : "NONE"}
            </span>
          </div>
          <div className="tp-tile-body" style={{ padding: "8px 0" }}>
            {latestCryptoValidation === null ? (
              <div
                style={{ padding: "12px 0", color: "var(--tp-fg-3)", fontSize: 12 }}
              >
                No crypto validation runs yet.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "flex-start",
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {latestCryptoValidation.strategyId}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--tp-fg-3)",
                        marginTop: 2,
                      }}
                    >
                      {shortDate(latestCryptoValidation.periodStart)} to{" "}
                      {shortDate(latestCryptoValidation.periodEnd)}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div
                      style={{
                        fontSize: 11,
                        color: "var(--tp-fg-3)",
                        textTransform: "uppercase",
                        letterSpacing: ".08em",
                      }}
                    >
                      PF
                    </div>
                    <div className="num" style={{ fontSize: 15, fontWeight: 600 }}>
                      {latestCryptoValidation.baselineProfitFactor ??
                        latestCryptoValidation.dashboardSummary.profitFactor ??
                        "-"}
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    gap: 6,
                    fontSize: 11,
                  }}
                >
                  {[
                    ["Lookahead", latestCryptoValidation.lookaheadPassed],
                    ["Recursive", latestCryptoValidation.recursiveStable],
                    ["Walk fwd", latestCryptoValidation.walkForwardPassed],
                  ].map(([label, value]) => (
                    <div
                      key={String(label)}
                      style={{
                        border: "1px solid var(--tp-border)",
                        borderRadius: 6,
                        padding: "6px 7px",
                        minWidth: 0,
                      }}
                    >
                      <div style={{ color: "var(--tp-fg-3)" }}>{label}</div>
                      <div
                        style={{
                          color:
                            value === true
                              ? "var(--tp-good)"
                              : value === false
                                ? "var(--tp-danger)"
                                : "var(--tp-fg-3)",
                          fontWeight: 600,
                          marginTop: 2,
                        }}
                      >
                        {validationCheckLabel(value as boolean | null)}
                      </div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 10,
                    fontSize: 12,
                  }}
                >
                  <span>
                    <span className="num">
                      {latestCryptoValidation.dashboardSummary.trades ??
                        latestCryptoValidation.baselineTrades ??
                        0}
                    </span>{" "}
                    trades
                  </span>
                  <span>
                    <span className="num">
                      {latestCryptoValidation.dashboardSummary.totalPnl ??
                        latestCryptoValidation.baselineTotalPnl ??
                        "0"}
                    </span>{" "}
                    P&L
                  </span>
                  <span>
                    <span className="num">
                      {latestCryptoValidation.dashboardSummary.maxDrawdown ?? "-"}
                    </span>{" "}
                    DD
                  </span>
                </div>

                {(latestCryptoValidation.pairLocks.length > 0 ||
                  latestCryptoValidation.protectionTriggers.length > 0) && (
                  <div
                    style={{
                      borderTop: "1px solid var(--tp-border)",
                      paddingTop: 8,
                      display: "flex",
                      flexDirection: "column",
                      gap: 5,
                      fontSize: 11,
                      color: "var(--tp-fg-3)",
                    }}
                  >
                    {latestCryptoValidation.pairLocks.slice(0, 2).map((lock) => (
                      <div
                        key={`${lock.symbol}-${lock.reason}-${lock.expiresAt ?? ""}`}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          gap: 8,
                        }}
                      >
                        <span style={{ color: "var(--tp-fg-1)", fontWeight: 600 }}>
                          {lock.symbol}
                        </span>
                        <span style={{ textAlign: "right" }}>
                          {lock.reason} until {shortDate(lock.expiresAt)}
                        </span>
                      </div>
                    ))}
                    {latestCryptoValidation.protectionTriggers
                      .slice(0, 2)
                      .map((trigger) => (
                        <div
                          key={trigger.reason}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <span>{trigger.reason}</span>
                          <span className="num">{trigger.count}</span>
                        </div>
                      ))}
                  </div>
                )}

                {latestCryptoValidation.strategyAttribution.length > 0 && (
                  <div style={{ fontSize: 11, color: "var(--tp-fg-3)" }}>
                    Top strategy:{" "}
                    <span style={{ color: "var(--tp-fg-1)", fontWeight: 600 }}>
                      {latestCryptoValidation.strategyAttribution[0].strategyId}
                    </span>
                    {" · "}
                    <span className="num">
                      {latestCryptoValidation.strategyAttribution[0].profitFactor}
                    </span>{" "}
                    PF
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="tp-tile">
          <div className="tp-tile-head">
            <span className="label">Baskets</span>
            <a
              className="action"
              onClick={() => {
                onConfigTab("baskets");
                onNav("config");
              }}
            >
              Manage →
            </a>
          </div>
          <div className="tp-tile-body" style={{ padding: "8px 0" }}>
            {baskets.length === 0 ? (
              <div
                style={{ padding: "12px 0", color: "var(--tp-fg-3)", fontSize: 12 }}
              >
                No baskets configured yet. Baskets group symbols by theme or
                sector and track aggregate exposure.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {baskets.map((b) => (
                  <div
                    key={b.id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      borderBottom: "1px solid var(--tp-border)",
                      paddingBottom: 8,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{b.name}</div>
                      <div style={{ fontSize: 11, color: "var(--tp-fg-3)", marginTop: 2 }}>
                        {b.members.slice(0, 6).join(" · ")}
                        {b.members.length > 6 && ` +${b.members.length - 6}`}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                      <div className="num" style={{ fontSize: 13 }}>
                        {b.exposure > 0 ? `${b.exposure.toFixed(0)}%` : "—"}
                        <span style={{ color: "var(--tp-fg-3)", fontSize: 11 }}>
                          {" "}/ {b.cap.toFixed(0)}% cap
                        </span>
                      </div>
                      {b.signals24h > 0 && (
                        <div style={{ fontSize: 11, color: "var(--tp-warn)" }}>
                          {b.signals24h} signal{b.signals24h !== 1 ? "s" : ""} 24h
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
